import _ from 'lodash'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import dayjsDurationPlugin from 'dayjs/plugin/duration'
import { LOCALSTORAGE_KEYS } from '@constants/index'
import { ACCOUNT_TYPE, ForefrontPopup } from '@interface/common'
import { DeletedMatchTimelineMatchIdMap, RegisteredMatchTimelineMap } from '@store/chattingStore'
import { SideBarTheme } from '@store/globalStore'
import { schema_chat_emoticon } from 'adv-lib/src/models/chat/chat-emoticon'

dayjs.extend(utc)
dayjs.extend(dayjsDurationPlugin)

export type StorageType = 'local' | 'session'

export default class StorageUtils {

  /**
   * LocalStorage 또는 SessionStorage 에 값을 저장 한다.
   *
   * @param {LOCALSTORAGE_KEYS} key
   * @param data
   * @param {StorageType} storageType local 혹은 session 스토리지 선택. 기본값: local
   */
  static save(key: LOCALSTORAGE_KEYS, data: any, storageType: StorageType = 'local'): void {
    let saveData
    if (data instanceof Date) {
      saveData = dayjs(data).utc().format()
    } else if (typeof data === 'string' || typeof data === 'boolean' || typeof data === 'number') {
      saveData = data
    } else {
      saveData = JSON.stringify(data)
    }
    switch (storageType) {
      case 'local':
        this.saveToLocal(key, saveData)
        break
      case 'session':
        this.saveToSession(key, saveData)
        break
      default:
        console.error('스토리지 키값 파라메타가 잘못되었습니다. (허용: local, session)', storageType)
        break
    }
  }

  static saveToLocal(key: LOCALSTORAGE_KEYS, data: any): void {
    localStorage.setItem(key, data)
  }

  static saveToSession(key: LOCALSTORAGE_KEYS, data: any): void {
    sessionStorage.setItem(key, data)
  }

  static delete(key: LOCALSTORAGE_KEYS, storageType: StorageType = 'local'): void {
    switch (storageType) {
      case 'local':
        this.deleteLocalKey(key)
        break
      case 'session':
        this.deleteSessionKey(key)
        break
    }
  }

  static deleteLocalKey(key: LOCALSTORAGE_KEYS): void {
    localStorage.removeItem(key)
  }

  static deleteSessionKey(key: LOCALSTORAGE_KEYS): void {
    sessionStorage.removeItem(key)
  }

  static getFromLocal(key: LOCALSTORAGE_KEYS): string | null {
    return localStorage.getItem(key)
  }

  static getFromSession(key: LOCALSTORAGE_KEYS): string | null {
    return sessionStorage.getItem(key)
  }

  static getValue(key: LOCALSTORAGE_KEYS, storageType: StorageType = 'local'): string | null {
    switch (storageType) {
      case 'local':
        return this.getFromLocal(key)
      case 'session':
        return this.getFromSession(key)
    }
  }

  static getIntValue(key: LOCALSTORAGE_KEYS, storageType: StorageType = 'local'): number {
    const value = this.getValue(key, storageType)
    if (!_.isNil(value) && !_.isNaN(Number(value))) {
      return Number(value)
    }
    return 0
  }

  static getBoolValue(key: LOCALSTORAGE_KEYS, storageType: StorageType = 'local'): boolean {
    const value = this.getValue(key, storageType)
    if (!_.isNil(value)) {
      return value === 'true' || Number(value) === 1
    }
    return false
  }

  static getJsonValue<T>(key: LOCALSTORAGE_KEYS, storageType: StorageType = 'local'): T | null {
    const value = this.getValue(key, storageType)
    try {
      if (!_.isNil(value)) {
        const jsonData = JSON.parse(value)
        return jsonData as T
      }
    } catch (error) {
      console.error(error)
    }
    return null
  }

  static getDateValue(key: LOCALSTORAGE_KEYS, storageType: StorageType = 'local'): Date | null {
    const value = this.getValue(key, storageType)
    if (!_.isNil(value)) {
      return dayjs(value).toDate()
    }
    return value
  }

  static saveUserPickPromotionBannerHide(): void {
    if (!this.isUserPickPromotionBannerHide()) {
      this.save(LOCALSTORAGE_KEYS.USER_PICK_PROMOTION_BANNER_HIDE_TIME, new Date().getTime())
      this.save(LOCALSTORAGE_KEYS.USER_PICK_PROMOTION_BANNER_HIDE, true)
    }
  }

  static isUserPickPromotionBannerHide(): boolean {
    const hideTime = this.getIntValue(LOCALSTORAGE_KEYS.USER_PICK_PROMOTION_BANNER_HIDE_TIME)
    if (hideTime < 1) {
      return false
    }
    return this.getBoolValue(LOCALSTORAGE_KEYS.USER_PICK_PROMOTION_BANNER_HIDE)
  }

  static isUserPickBestPickBannerHide(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE)
  }

  static isMatchupBottomBannerHide(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_HIDE)
  }

  static saveUserPickBestPickBannerHideTime(): void {
    this.save(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE_TIME, new Date().getTime())
  }

  static saveMatchupBottomBannerShowNextTime(): void {
    this.save(
      LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_SHOW_NEXT_TIME,
      dayjs().add(1, 'days').startOf('days').valueOf(),
    )
  }

  static saveUserPickBestPickBannerHide7Days(): void {
    this.save(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE_7DAYS,
      dayjs().add(7, 'days').startOf('days').valueOf(),
    )
  }

  static saveMatchupBottomBannerShowNext7Days(): void {
    this.removeMatchupBottomBannerHide()
    this.save(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_SHOW_NEXT_7DAYS,
      dayjs().add(7, 'days').startOf('days').valueOf(),
    )
  }

  static saveLastUserPickBestPickId(userPickId: number): void {
    this.save(LOCALSTORAGE_KEYS.LAST_USER_PICK_BEST_PICK_ID, userPickId)
  }

  static getLastUserPickBestPickId(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.LAST_USER_PICK_BEST_PICK_ID)
  }

  static removeLastUserPickBestPickId(): void {
    this.delete(LOCALSTORAGE_KEYS.LAST_USER_PICK_BEST_PICK_ID)
  }

  static getUserPickBestPickBannerHideTime(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE_TIME)
  }

  static getMatchupBottomBannerShowNextTime(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_SHOW_NEXT_TIME)
  }

  static getUserPickBestPickBannerHide7Days(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE_7DAYS)
  }

  static getMatchupBottomBannerShowNext7Days(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_SHOW_NEXT_7DAYS)
  }

  static saveUserPickBestPickBannerHide(): void {
    this.save(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE, true)
    this.saveUserPickBestPickBannerHideTime()
  }

  static removeUserPickBestUserPickBannerHide(): void {
    this.delete(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE)
    this.delete(LOCALSTORAGE_KEYS.USER_PICK_BEST_PICK_BANNER_HIDE_TIME)
  }

  static saveMatchupBottomBannerHide(): void {
    this.save(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_HIDE, true)
    this.saveMatchupBottomBannerShowNextTime()
  }

  static removeMatchupBottomBannerHide(): void {
    this.delete(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_SHOW_NEXT_7DAYS)
    this.delete(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_HIDE)
    this.delete(LOCALSTORAGE_KEYS.MATCHUP_BOTTOM_BANNER_SHOW_NEXT_TIME)
  }

  static saveGiftBoxNoticeTextImageHideOnMobileMenu(): void {
    this.save(LOCALSTORAGE_KEYS.GIFT_BOX_NOTICE_TEXT_IMAGE_HIDE_ON_MOBILE_MENU, true)
  }

  static isGiftBoxNoticeTextImageHideOnMobileMenu(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.GIFT_BOX_NOTICE_TEXT_IMAGE_HIDE_ON_MOBILE_MENU)
  }

  // 계정·CI를 구분하지 않는 브라우저 공통 시청 이력이다. 강제 재생을 끝까지 봤을 때만 기록한다.
  static isUsageGuideVideoWatched(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.USAGE_GUIDE_VIDEO_WATCHED)
  }

  static saveUsageGuideVideoWatched(): void {
    this.save(LOCALSTORAGE_KEYS.USAGE_GUIDE_VIDEO_WATCHED, true)
  }

  static saveForeFrontPopupShowList(data: ForefrontPopup[]): void {
    this.save(LOCALSTORAGE_KEYS.FORE_FRONT_POPUP_SHOW_LIST, data)
  }

  static getForeFrontPopupList(): ForefrontPopup[] {
    return this.getJsonValue<ForefrontPopup[]>(LOCALSTORAGE_KEYS.FORE_FRONT_POPUP_SHOW_LIST) ?? []
  }

  static saveLastClubId(lastClubId: string): void {
    this.save(LOCALSTORAGE_KEYS.LAST_CLUB_ID, lastClubId)
  }

  static getLastClubId(): string {
    return this.getValue(LOCALSTORAGE_KEYS.LAST_CLUB_ID)
  }

  static deleteLastClubId(): void {
    this.delete(LOCALSTORAGE_KEYS.LAST_CLUB_ID)
  }

  static savePickPromotionButtonLastShowedAt(): void {
    this.save(LOCALSTORAGE_KEYS.PICK_PROMOTION_BUTTON_LAST_SHOWED_AT, dayjs().valueOf())
  }

  static getPickPromotionButtonLastShowedAt(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.PICK_PROMOTION_BUTTON_LAST_SHOWED_AT)
  }

  static isPickPromotionButtonHide(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.PICK_PROMOTION_BUTTON_HIDE)
  }

  static savePickPromotionButtonHide(): void {
    if (!this.isPickPromotionButtonHide()) {
      this.save(LOCALSTORAGE_KEYS.PICK_PROMOTION_BUTTON_HIDE, true)
    }
  }

  static isShowUserPickFirstInfo(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.ADV_SHOW_USER_PICK_FIRST_INFO)
  }

  static saveShowUserPickFirstInfo(): void {
    if (!this.isShowUserPickFirstInfo()) {
      this.save(LOCALSTORAGE_KEYS.ADV_SHOW_USER_PICK_FIRST_INFO, true)
    }
  }

  static saveSignTypeAndUserId(signType: ACCOUNT_TYPE, userId: string): void {
    this.saveSignType(signType)
    this.saveUserId(userId)
  }

  static removeSignTypeAndUserId(): void {
    this.delete(LOCALSTORAGE_KEYS.SIGN)
    this.delete(LOCALSTORAGE_KEYS.USERID)
  }

  static saveSignType(data: ACCOUNT_TYPE): void {
    this.save(LOCALSTORAGE_KEYS.SIGN, data)
  }

  static saveUserId(data: string): void {
    this.save(LOCALSTORAGE_KEYS.USERID, data)
  }

  static removeUserId(): void {
    this.delete(LOCALSTORAGE_KEYS.USERID)
  }

  static getSignType(): string {
    return this.getValue(LOCALSTORAGE_KEYS.SIGN)
  }

  static getUserId(): string {
    return this.getValue(LOCALSTORAGE_KEYS.USERID)
  }

  static saveScreenHeight(data: number): void {
    this.save(LOCALSTORAGE_KEYS.SCREEN_HEIGHT, data)
  }

  static getScreenHeight(): string {
    return this.getValue(LOCALSTORAGE_KEYS.SCREEN_HEIGHT)
  }

  static saveKeyboardHeight(data: number): void {
    this.save(LOCALSTORAGE_KEYS.KEYBOARD_HEIGHT, data)
  }

  static getKeyboardHeight(): string {
    return this.getValue(LOCALSTORAGE_KEYS.KEYBOARD_HEIGHT)
  }

  static getNoticeHide(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.NOTICE_HIDE)
  }

  static saveNoticeHide(): void {
    const next = dayjs().add(8, 'days')
      .hour(0).minute(0).second(0).millisecond(0).valueOf()
    this.save(LOCALSTORAGE_KEYS.NOTICE_HIDE, next)
  }

  static getGodBlessItemAutoSelectionGuidePopupShowedAt(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.GOD_BLESS_ITEM_AUTO_SELECTION_GUIDE_POPUP_SHOWED_AT)
  }

  static saveGodBlessItemAutoSelectionGuidePopupShowedAt(): void {
    this.save(LOCALSTORAGE_KEYS.GOD_BLESS_ITEM_AUTO_SELECTION_GUIDE_POPUP_SHOWED_AT, dayjs().valueOf())
  }

  static saveGodBlessItemAutoSelectionFreeClubEnabled(data: boolean): void {
    this.save(LOCALSTORAGE_KEYS.GOD_BLESS_ITEM_AUTO_SELECTION_FREE_CLUB_ENABLED, data)
  }

  static isGodBlessItemAutoSelectionFreeClubEnabled(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.GOD_BLESS_ITEM_AUTO_SELECTION_FREE_CLUB_ENABLED)
  }

  static saveGodBlessItemAutoSelectionMemberShipCLubEnabled(data: boolean): void {
    this.save(LOCALSTORAGE_KEYS.GOD_BLESS_ITEM_AUTO_SELECTION_MEMBERSHIP_CLUB_ENABLED, data)
  }

  static isGodBlessItemAutoSelectionMemberShipClubEnabled(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.GOD_BLESS_ITEM_AUTO_SELECTION_MEMBERSHIP_CLUB_ENABLED)
  }

  static saveBettingSlipKeepCartExplainImageDisplayedUserPromiseIds(userIds: string[]): void {
    this.save(LOCALSTORAGE_KEYS.BETTING_SLIP_KEEP_CART_EXPLAIN_IMAGE_DISPLAYED_USER_PROMISE_IDS, userIds)
  }

  static getBettingSlipKeepCartExplainImageDisplayedUserPromiseIds(): string[] {
    return this.getJsonValue<string[]>(
      LOCALSTORAGE_KEYS.BETTING_SLIP_KEEP_CART_EXPLAIN_IMAGE_DISPLAYED_USER_PROMISE_IDS,
    ) ?? []
  }

  static saveAuthKey(authKey: string): void {
    this.save(LOCALSTORAGE_KEYS.AUTH_KEY, authKey)
  }

  static removeAuthKey(): void {
    this.delete(LOCALSTORAGE_KEYS.AUTH_KEY)
  }

  static saveTimelineMatchIdList(matchIds: RegisteredMatchTimelineMap): void {
    this.save(LOCALSTORAGE_KEYS.TIMELINE_MATCH_ID_LIST, matchIds)
  }

  static getTimelineMatchIdList(): RegisteredMatchTimelineMap {
    return this.getJsonValue<RegisteredMatchTimelineMap>(LOCALSTORAGE_KEYS.TIMELINE_MATCH_ID_LIST)
  }

  static saveDeletedTimeLineMatchIdList(matchIds: DeletedMatchTimelineMatchIdMap): void {
    this.save(LOCALSTORAGE_KEYS.DELETED_TIMELINE_MATCH_ID_LIST, matchIds)
  }

  static getDeletedTimelineMatchIdList(): DeletedMatchTimelineMatchIdMap {
    return this.getJsonValue<DeletedMatchTimelineMatchIdMap>(LOCALSTORAGE_KEYS.DELETED_TIMELINE_MATCH_ID_LIST)
  }

  static removeTimelineMatchIdList(): void {
    this.delete(LOCALSTORAGE_KEYS.TIMELINE_MATCH_ID_LIST)
  }

  static saveLastVisibleCompanyPickNoticeId(id: string): void {
    this.save(LOCALSTORAGE_KEYS.LAST_VISIBLE_COMPANY_PICK_NOTICE_ID, id)
  }

  static saveLastVisibleCompanyPickNoticeCompanyPickIds(ids: string[]): void {
    this.save(LOCALSTORAGE_KEYS.LAST_VISIBLE_COMPANY_PICK_NOTICE_COMPANY_PICK_IDS, ids)
  }

  static getLastVisibleCompanyPickNoticeId(): string {
    return this.getValue(LOCALSTORAGE_KEYS.LAST_VISIBLE_COMPANY_PICK_NOTICE_ID)
  }

  static getLastVisibleCompanyPickNoticeCompanyPickIds(): string[] {
    return this.getJsonValue<string[]>(LOCALSTORAGE_KEYS.LAST_VISIBLE_COMPANY_PICK_NOTICE_COMPANY_PICK_IDS)
  }

  static saveSideBarTheme(theme: SideBarTheme): void {
    this.save(LOCALSTORAGE_KEYS.SIDE_BAR_THEME, theme)
  }

  static saveNavOpenKeys(openKeys: string[]): void {
    this.save(LOCALSTORAGE_KEYS.NAV_OPEN_KEYS, openKeys)
  }

  static saveLastSelectedGodBlessItemPropertyId(propertyId: number): void {
    this.save(LOCALSTORAGE_KEYS.LAST_SELECTED_GOD_BLESS_ITEM_PROPERTY_ID, propertyId)
  }

  static getLastSelectionGodBlessItemPropertyId(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.LAST_SELECTED_GOD_BLESS_ITEM_PROPERTY_ID)
  }

  static saveCasinoAdvertisementLastShowedAt(): void {
    this.save(LOCALSTORAGE_KEYS.CASINO_ADVERTISEMENT_LAST_SHOWED_AT, new Date())
  }

  static getCasinoAdvertisementLastShowedAt(): Date {
    return this.getDateValue(LOCALSTORAGE_KEYS.CASINO_ADVERTISEMENT_LAST_SHOWED_AT)
  }

  static saveNotSupportPolling(value: boolean): void {
    this.save(LOCALSTORAGE_KEYS.NOT_SUPPORT_POLLING, value)
  }

  static isNotSupportPolling(): boolean {
    return this.getBoolValue(LOCALSTORAGE_KEYS.NOT_SUPPORT_POLLING)
  }

  static saveMatchEtcFilterAlertUpdatedTime(updatedTime: number): void {
    this.save(LOCALSTORAGE_KEYS.MATCH_ETC_FILTER_ALERT_UPDATED_AT, updatedTime)
  }

  static getMatchEtcFilterAlertUpdatedTime(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.MATCH_ETC_FILTER_ALERT_UPDATED_AT)
  }

  static saveAccountTransferInfoHideTime(): void {
    this.save(LOCALSTORAGE_KEYS.ACCOUNT_TRANSFER_INFO_HIDE_TIME, new Date().getTime())
  }

  static getShowAccountTransferInfo(): number {
    return this.getIntValue(LOCALSTORAGE_KEYS.ACCOUNT_TRANSFER_INFO_HIDE_TIME)
  }

  static getChatEmoticonListByVersion(version: number): Partial<schema_chat_emoticon>[] {
    const data
      = this.getJsonValue<Record<string, Partial<schema_chat_emoticon>[]>>(LOCALSTORAGE_KEYS.CHAT_EMOTICON_LIST) ?? {}
    return data[version] ? data[version] : []
  }

  static saveChatEmoticonListByVersion(version: number, emoticonList: Partial<schema_chat_emoticon>[]): void {
    const data
      = this.getJsonValue<Record<string, Partial<schema_chat_emoticon>[]>>(LOCALSTORAGE_KEYS.CHAT_EMOTICON_LIST) ?? {}
    data[version] = emoticonList
    StorageUtils.save(LOCALSTORAGE_KEYS.CHAT_EMOTICON_LIST, data)
  }

  static deleteAllChatEmoticonList(): void {
    StorageUtils.delete(LOCALSTORAGE_KEYS.CHAT_EMOTICON_LIST)
  }

  static saveCheerForTeamEffectEnabled(enabled: boolean): void {
    StorageUtils.save(LOCALSTORAGE_KEYS.CHEER_FOR_TEAM_EFFECT_ENABLED, enabled)
  }

  static getCheerForTeamEffectEnabled(): boolean {
    const value = StorageUtils.getValue(LOCALSTORAGE_KEYS.CHEER_FOR_TEAM_EFFECT_ENABLED)
    if (_.isNil(value)) {
      return true
    }

    return value === 'true' || Number(value) === 1
  }

  static savePopularSearchTermNewsReadUrlMap(urlMap: { [key: string]: Date }): void {
    this.save(LOCALSTORAGE_KEYS.POPULAR_SEARCH_TERM_NEWS_READ_URL_MAP, urlMap)
  }

  static getPopularSearchTermNewsReadUrlMap<T>(): T | null {
    return this.getJsonValue<T>(LOCALSTORAGE_KEYS.POPULAR_SEARCH_TERM_NEWS_READ_URL_MAP) || {} as T
  }

  static saveSportTalkBubbleMessageShowTime(): void {
    this.save(LOCALSTORAGE_KEYS.SPORT_TALK_BUBBLE_MESSAGE_SHOW_TIME, new Date().getTime())
  }

  static getSportTalkBubbleMessageEnabled(): boolean {
    const value = this.getIntValue(LOCALSTORAGE_KEYS.SPORT_TALK_BUBBLE_MESSAGE_SHOW_TIME)

    if (value > 0) {
      const showDate = dayjs(value)
      const today = dayjs()
      const dayOfWeek = today.day()
      const monday = today.subtract((dayOfWeek + 6) % 7, 'day').startOf('day')

      return showDate.isBefore(monday)
    }

    return true
  }
}
